import { FunctionComponent, useState } from 'react';
import { Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import ArrowDownOnSquareIcon from '@heroicons/react/24/solid/ArrowDownOnSquareIcon';
import { StarjackTable } from '@/components/Table/v2/Table';
import {
    GetCustomerSearchParams,
    useGetCustomersQuery,
} from '@/api/v2/customer';
import { Customer } from '@/models/customer';
import useDebouncedEffect from '@/models/useDebouncedEffect';
import { downloadContentGeneric } from '@/utils';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import { urlBuilder } from '@/api/utils';

import { LoadingButton } from '@mui/lab';
import { formatStarjackDate } from '@/utils/time';
import { useNavigate } from 'react-router-dom';
import { CustomersSearch } from '@/pages/customers/components/CustomersSearch';

const downloadData = async (
    userSearchTerm: string,
    jwt: string
): Promise<string | null> => {
    const response = await fetch(
        urlBuilder(
            '/auth',
            `customers/customers.xlsx${
                userSearchTerm
                    ? `?search=${encodeURIComponent(userSearchTerm)}`
                    : ''
            }`
        ),
        {
            method: 'GET',
            headers: {
                Authorization: `bearer ${jwt}`,
            },
        }
    );

    if (response.ok) {
        return URL.createObjectURL(
            new Blob([await response.blob()], {
                type: 'data:application/vnd.ms-excel;base64',
            })
        );
    } else {
        // as long as there is no detailed error message - null is enough
        return null;
    }
};

export const CustomersContent: FunctionComponent = () => {
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [searchParams, setSearchParams] = useState<GetCustomerSearchParams>({
        page: 0,
        size: 10,
    });
    const [downloadLoading, setDownloadLoading] = useState(false);
    const { data, isLoading } = useGetCustomersQuery(searchParams);
    const { addNotification } = useNotification();
    const { jwt } = useAuthentication();
    const theme = useTheme();
    const navigate = useNavigate();

    const onDownloadClick = async () => {
        setDownloadLoading(true);

        try {
            const fileData = await downloadData(userSearchTerm, jwt);

            if (fileData === null) {
                throw new Error('Download failed');
            }

            downloadContentGeneric('Kunden.xlsx', fileData);
        } catch {
            addNotification({
                type: 'error',
                message:
                    'Fehler beim Download. Bitte später noch einmal versuchen',
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    useDebouncedEffect(
        () => {
            if (userSearchTerm?.length >= 3) {
                setSearchParams((prevState) => ({
                    ...prevState,
                    page: 0,
                    search: userSearchTerm,
                }));
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                setSearchParams(({ search, ...prevState }) => ({
                    ...prevState,
                    page: 0,
                }));
            }
        },
        250,
        [userSearchTerm]
    );

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Kunden
            </Typography>

            <Stack alignItems='flex-start' spacing={theme.page.verticalSpacing}>
                <LoadingButton
                    onClick={onDownloadClick}
                    loading={downloadLoading}
                    color='inherit'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <ArrowDownOnSquareIcon />
                        </SvgIcon>
                    }
                >
                    Exportieren
                </LoadingButton>

                <CustomersSearch onChange={setUserSearchTerm} />

                <StarjackTable<Customer>
                    count={data?.totalElements || 0}
                    items={data?.content ?? []}
                    columns={[
                        {
                            label: 'E-Mail',
                            itemTransform: (customer) => customer.email,
                        },
                        {
                            label: 'Vorname',
                            itemTransform: (customer) => customer.firstname,
                        },
                        {
                            label: 'Nachname',
                            itemTransform: (customer) => customer.lastname,
                        },
                        {
                            label: 'Marketing-Akzeptanzdatum',
                            itemTransform: (customer) =>
                                customer?.marketingAcceptance
                                    ? formatStarjackDate(
                                          customer.marketingAcceptanceDate
                                      )
                                    : '-',
                        },
                    ]}
                    itemId='id'
                    rowsPerPage={searchParams.size}
                    currentPage={searchParams.page}
                    onRowsPerPageChange={(nextValue) => {
                        setSearchParams((prevState) => ({
                            ...prevState,
                            size: nextValue,
                        }));
                    }}
                    onPageChange={(nextPage) => {
                        setSearchParams((prevState) => ({
                            ...prevState,
                            page: nextPage,
                        }));
                    }}
                    loading={isLoading}
                    rowsSelectable={false}
                    onRowClick={(customer) => {
                        navigate(`/kunden/details?id=${customer.id}`);
                    }}
                />
            </Stack>
        </>
    );
};
